import { Link } from 'react-router-dom';
import {
  endpointHref,
  endpointsByGroup,
  type ApiEndpoint,
  type ApiMethod,
} from '../api-endpoints';
import { useDocsPage } from '../hooks/useDocsPage';

const GROUP_BLURB: Record<string, string> = {
  Ingestion: 'High-volume signed event streams',
  Usage: 'Direct usage mutations with idempotency',
  Discovery: 'Read subscriptions, meters, and regions',
  Webhooks: 'Push payment status into Revvly',
  GraphQL: 'Curated public schema for clients, subscriptions and prospects',
  Forms: 'Submit answers to a published form from your backend',
  Support: 'Sign users in and build your own support chat',
};

function methodClass(method: ApiMethod): string {
  return `api-method api-method-${method.toLowerCase()}`;
}

function EndpointRow({ endpoint }: { endpoint: ApiEndpoint }) {
  return (
    <Link className="api-index-row" to={endpointHref(endpoint)}>
      <span className={methodClass(endpoint.method)}>{endpoint.method}</span>
      <span className="api-index-main">
        <span className="api-index-path">{endpoint.path}</span>
        <span className="api-index-summary">{endpoint.summary}</span>
      </span>
      <span className="api-index-hash" aria-hidden="true">
        #{endpoint.id}
      </span>
    </Link>
  );
}

export function ApiIndex() {
  useDocsPage('API Reference — Revvly Docs');
  const groups = endpointsByGroup();

  return (
    <div className="tn-page">
      <header className="dp-header">
        <span className="rv-eyebrow-mini">API Reference</span>
        <h1>
          Every public <em>endpoint</em>, in one place.
        </h1>
        <p className="dp-oneliner">
          Browse the full public API surface documented here. Each row links to its
          reference section — share a deep link with the <span className="api-inline">#</span>{' '}
          hash on any endpoint.
        </p>
      </header>

      <section className="dp-section">
        <h2>Reference pages</h2>
        <p className="dp-section-sub">
          Full request and response docs live on dedicated pages. Jump there for signing,
          field tables, and examples — or pick an endpoint below to land on the exact section.
        </p>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/api/events" style={{ color: 'inherit', textDecoration: 'none' }}>
              Event Ingestion API ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/api/product-events"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Product Events API ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/api/invoice-payments"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Invoice Payment Webhook ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/api/graphql-public"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Public GraphQL API ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/api/external-references"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              External references &amp; discovery ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/usage-reporting"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Usage reporting guide ›
            </Link>
          </li>
        </ul>
      </section>

      {groups.map(({ group, endpoints }) => (
        <section className="dp-section" key={group} id={group.toLowerCase()}>
          <h2>{group}</h2>
          <p className="dp-section-sub">{GROUP_BLURB[group]}</p>
          <div className="api-index-list">
            {endpoints.map((endpoint) => (
              <EndpointRow key={`${endpoint.docsPath}#${endpoint.id}`} endpoint={endpoint} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
